import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { DistributionModuleConfig } from '@/types/moduleConfig';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Close, Search, West as WestIcon } from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { ASSET_STATUS_KEYS } from '@equip/utils';
import { WorkflowModalLayout } from '@/components/shared/workflows/WorkflowModalLayout';
import { WorkflowCartRow } from '@/components/shared/workflows/WorkflowCartRow';
import { useAssetInventory, type AssetInventoryGroup } from '@/hooks/useAssetInventory';
import { EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { useAuth } from '@/hooks/useAuth';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { groupTransfersShippingCartItems } from '@/components/transfers-shipping/groupTransfersShippingCartItems';
import {
  useTransfersShippingCart,
  useAddToTransfersShippingCart,
  useRemoveFromTransfersShippingCart,
  useClearTransfersShippingCart,
  useCompleteTransfer,
  useCompleteShipment,
  type TransfersShippingWorkflowType,
} from '@/hooks/mutations/transfers-shipping';
import { useQueryClient } from '@tanstack/react-query';
import { cartQueryKeys } from '@/hooks/queries/cart/useCartQueries';
import type {
  TransfersShippingType,
  TransfersShippingCompleteResult,
} from '@/services/transfers-shipping';
import { sortTransferInventoryRows } from '@/services/transfers-shipping';
import { useNotifications } from '@/stores/useNotificationStore';
import { documentService } from '@/services/documents';
import type { DocumentPreviewResponse } from '@/services/documents';
import { openBase64PdfInWindow, openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { DocumentFieldsModal } from '@/components/documents/DocumentFieldsModal';
import { SignatureCaptureDialog } from '@/components/shared/SignatureCaptureDialog';
import { toYYYYMMDD } from '@/utils/dateFormat';
import { httpClient } from '@/utils/httpClient';
import { multiWordMatch } from '@/utils/multiWordMatch';

/** Fetch unit prices and units of issue for a set of catalog entity IDs. */
async function fetchCatalogData(catalogIds: string[]) {
  const priceMap = new Map<string, number>();
  const unitOfIssueMap = new Map<string, string>();
  if (catalogIds.length === 0) return { priceMap, unitOfIssueMap };

  try {
    const catalogs = await httpClient.get<
      Array<{ id: string; entity_data: { unit_price?: number; unit_of_issue?: string } }>
    >(
      `/api/v1/entities?entity_type=eq.catalog&id=in.(${catalogIds.join(',')})&select=id,entity_data`,
    );
    for (const cat of catalogs) {
      const price = cat.entity_data?.unit_price;
      if (typeof price === 'number') {
        priceMap.set(cat.id, price);
      }
      const uoi = cat.entity_data?.unit_of_issue;
      if (typeof uoi === 'string' && uoi.trim().length > 0) {
        unitOfIssueMap.set(cat.id, uoi.trim());
      }
    }
  } catch {
    // Proceed without catalog data — price/UOI fields will use defaults
  }

  return { priceMap, unitOfIssueMap };
}

export interface TransfersShippingModalProps {
  open: boolean;
  onClose: () => void;
  transferType: TransfersShippingType;
}

const clampQuantity = (value: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return 1;
  }
  return Math.min(Math.max(Math.floor(value), 1), Math.max(max, 1));
};

type TransferReasonKey = 'mission' | 'lateral' | 'other';

const TRANSFER_REASON_OPTIONS: Array<{ value: TransferReasonKey; label: string }> = [
  {
    value: 'mission',
    label: 'To Transfer Accountability from HR ___ to HR ____ in support of mission requirement.',
  },
  {
    value: 'lateral',
    label: 'Lateral transfer required to reduce excess on HR _____ and fill shortage on HR _____.',
  },
  {
    value: 'other',
    label: 'Other',
  },
];

const buildDa3161ItemsWithReasonAtRow4 = (
  lineItems: Array<{
    line_number: string;
    stock_number: string;
    description: string;
    unit_of_issue: string;
    quantity: number | string;
    code: string;
    supply_action: number | string;
    actual_price: number | undefined;
    total_cost: number | undefined;
  }>,
  reason: string,
): Array<Record<string, unknown>> => {
  const normalizedReason = reason.trim();
  if (!normalizedReason) {
    return lineItems;
  }

  const reasonRow: Record<string, unknown> = {
    line_number: '',
    stock_number: '',
    description: normalizedReason,
    unit_of_issue: 'EA',
    quantity: '',
    code: '',
    supply_action: '',
    actual_price: undefined,
    total_cost: undefined,
  };

  const reasonIndex = 3;
  const withReason: Array<Record<string, unknown>> = [...lineItems];

  while (withReason.length <= reasonIndex) {
    withReason.push({
      line_number: '',
      stock_number: '',
      description: '',
      unit_of_issue: '',
      quantity: '',
      code: '',
      supply_action: '',
      actual_price: undefined,
      total_cost: undefined,
    });
  }

  const existingRow = withReason[reasonIndex] as Record<string, unknown> | undefined;
  withReason[reasonIndex] = {
    ...(existingRow ?? {}),
    ...reasonRow,
  };

  return withReason;
};

export const TransfersShippingModal: React.FC<TransfersShippingModalProps> = ({
  open,
  onClose,
  transferType,
}) => {
  const { config: distributionConfig } = useModuleConfig<DistributionModuleConfig>('distribution');
  const distributionLabels = distributionConfig.features.transfers_shipping;
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const workflowType: TransfersShippingWorkflowType = transferType;
  const isTransfer = transferType === 'transfer';
  const notifications = useNotifications();
  const person = usePersonLabels('front_window');
  const queryClient = useQueryClient();

  const { data: cartData } = useTransfersShippingCart(userId, workflowType, {
    enabled: open && !!userId,
  });
  const { mutateAsync: addToCart, isPending: isAdding } =
    useAddToTransfersShippingCart(workflowType);
  const { mutateAsync: removeFromCart, isPending: isRemoving } =
    useRemoveFromTransfersShippingCart(workflowType);
  const { mutateAsync: clearCart } = useClearTransfersShippingCart(workflowType);

  const {
    data = [],
    isLoading,
    isError,
    refetch,
  } = useAssetInventory({
    status_key: `eq.${ASSET_STATUS_KEYS.AVAILABLE}`,
    ...EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL,
  });

  const transferMutation = useCompleteTransfer();
  const shipmentMutation = useCompleteShipment();
  const completeMutation = isTransfer ? transferMutation : shipmentMutation;

  const [searchText, setSearchText] = useState('');
  const [qtyDrafts, setQtyDrafts] = useState<Record<string, number>>({});
  const [fieldsModalOpen, setFieldsModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [previewData, setPreviewData] = useState<DocumentPreviewResponse | null>(null);
  const [transferReason, setTransferReason] = useState<TransferReasonKey | ''>('');
  const [hrFrom, setHrFrom] = useState('');
  const [hrTo, setHrTo] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [showTransferReasonErrors, setShowTransferReasonErrors] = useState(false);
  const [signatureCaptureOpen, setSignatureCaptureOpen] = useState(false);
  const [capturedSignature, setCapturedSignature] = useState<string | null>(null);
  const [signatureAcknowledged, setSignatureAcknowledged] = useState(false);
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [isForceClosing, setIsForceClosing] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const pendingResultRef = useRef<TransfersShippingCompleteResult | null>(null);
  const pendingPayloadRef = useRef<{ documentType: string; payload: Record<string, unknown> } | null>(null);
  const pendingReservationIdRef = useRef<string | null>(null);
  const docWindowRef = useRef<Window | null>(null);

  const isPending = isAdding || isRemoving || completeMutation.isPending;

  const reservedAssetIds = useMemo(() => {
    if (!cartData?.assets) return new Set<string>();
    return new Set(cartData.assets.map((asset) => asset.entity.id));
  }, [cartData?.assets]);

  const cartItems = useMemo(() => {
    if (!cartData?.assets) return [];
    return groupTransfersShippingCartItems(cartData.assets);
  }, [cartData?.assets]);

  const cartCount = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.quantity, 0),
    [cartItems],
  );

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const matchingRows = !query
      ? data
      : data.filter((row) => {
          const parts = [
            row.asset_name,
            row.lin ?? '',
            row.nsn ?? '',
            row.location_name ?? '',
            row.location_path ?? '',
          ];
          return multiWordMatch(parts.join(' '), query);
        });

    return sortTransferInventoryRows(matchingRows);
  }, [data, searchText]);

  const getAddableQty = useCallback(
    (row: AssetInventoryGroup) => {
      return row.asset_ids.reduce((count, id) => count + (reservedAssetIds.has(id) ? 0 : 1), 0);
    },
    [reservedAssetIds],
  );

  const resetFormState = useCallback(() => {
    setSearchText('');
    setQtyDrafts({});
    setTransferReason('');
    setHrFrom('');
    setHrTo('');
    setOtherReason('');
    setShowTransferReasonErrors(false);
    setSignatureCaptureOpen(false);
    setCapturedSignature(null);
    setSignatureAcknowledged(false);
  }, []);

  const forceClose = useCallback(() => {
    setShowExitPrompt(false);
    setIsForceClosing(true);
  }, []);

  const handleClose = useCallback(() => {
    if (cartCount > 0) {
      setShowExitPrompt(true);
    } else {
      resetFormState();
      onClose();
    }
  }, [cartCount, resetFormState, onClose]);

  const handleSave = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
    resetFormState();
    forceClose();
  }, [queryClient, resetFormState, forceClose]);

  const handleCancelTransfer = useCallback(async () => {
    if (isCanceling) return;
    setIsCanceling(true);
    try {
      if (userId && cartData?.cart) {
        await clearCart({ userId, cartId: cartData.cart.id });
        void queryClient.invalidateQueries({ queryKey: cartQueryKeys.activeAll() });
      }
      resetFormState();
      forceClose();
    } catch {
      notifications.error('Error', `Failed to cancel the ${isTransfer ? 'transfer' : 'shipment'}.`);
    } finally {
      setIsCanceling(false);
    }
  }, [isCanceling, userId, cartData?.cart, clearCart, queryClient, resetFormState, forceClose, notifications, isTransfer]);

  const requiresHrValues = transferReason === 'mission' || transferReason === 'lateral';
  const transferReasonValidation = useMemo(() => {
    if (!isTransfer) {
      return {
        valid: true,
        reasonError: false,
        hrFromError: false,
        hrToError: false,
        otherError: false,
      };
    }

    const reasonError = transferReason === '';
    const hrFromError = requiresHrValues && hrFrom.trim().length === 0;
    const hrToError = requiresHrValues && hrTo.trim().length === 0;
    const otherError = transferReason === 'other' && otherReason.trim().length === 0;

    return {
      valid: !(reasonError || hrFromError || hrToError || otherError),
      reasonError,
      hrFromError,
      hrToError,
      otherError,
    };
  }, [isTransfer, transferReason, requiresHrValues, hrFrom, hrTo, otherReason]);

  const transferReasonStatement = useMemo(() => {
    if (transferReason === 'mission') {
      return `To Transfer Accountability from HR ${hrFrom.trim()} to HR ${hrTo.trim()} in support of mission requirement.`;
    }
    if (transferReason === 'lateral') {
      return `Lateral transfer required to reduce excess on HR ${hrFrom.trim()} and fill shortage on HR ${hrTo.trim()}.`;
    }
    if (transferReason === 'other') {
      return otherReason.trim();
    }
    return '';
  }, [transferReason, hrFrom, hrTo, otherReason]);

  const signaturePreviewSrc = useMemo(
    () => (capturedSignature ? `data:image/png;base64,${capturedSignature}` : undefined),
    [capturedSignature],
  );

  const handleQtyDraftChange = useCallback((rowId: string, rawValue: string, max: number) => {
    const parsed = Number.parseInt(rawValue, 10);
    setQtyDrafts((prev) => ({
      ...prev,
      [rowId]: clampQuantity(Number.isFinite(parsed) ? parsed : 1, max),
    }));
  }, []);

  const handleCaptureSignature = useCallback(() => {
    setSignatureCaptureOpen(true);
  }, []);

  const handleSignatureSaved = useCallback(
    (signatureBase64Png: string) => {
      setCapturedSignature(signatureBase64Png);
      setSignatureAcknowledged(true);
      setSignatureCaptureOpen(false);
      notifications.success('Signature captured', `${person.label} signature saved.`);
    },
    [notifications],
  );

  const handleClearSignature = useCallback(() => {
    setCapturedSignature(null);
    setSignatureAcknowledged(false);
  }, []);

  const handleSkipSignature = useCallback(() => {
    setCapturedSignature(null);
    setSignatureAcknowledged(true);
  }, []);

  const handleAddToCart = useCallback(
    async (row: AssetInventoryGroup) => {
      if (!userId) return;

      const availableAssetIds = row.asset_ids.filter((id) => !reservedAssetIds.has(id));
      const draft = qtyDrafts[row.id] ?? Math.max(availableAssetIds.length, 1);
      const quantity = clampQuantity(draft, availableAssetIds.length);
      const assetIdsToAdd = availableAssetIds.slice(0, quantity);

      if (assetIdsToAdd.length === 0) return;

      await addToCart({ userId, assetIds: assetIdsToAdd });
    },
    [userId, qtyDrafts, reservedAssetIds, addToCart],
  );

  const handleRemoveCartItem = useCallback(
    async (assetIds: string[]) => {
      if (!userId) return;
      await removeFromCart({ userId, assetIds });
    },
    [userId, removeFromCart],
  );

  const generateAndOpen = useCallback(
    async (
      documentType: string,
      payload: Record<string, unknown>,
      result: TransfersShippingCompleteResult,
    ) => {
      const reservationId = pendingReservationIdRef.current;

      const docWindow = preOpenWindow();
      docWindowRef.current = docWindow;
      let docFailed = false;

      try {
        const docResult = await documentService.generateDocument({
          documentType,
          payload: { ...payload, transactionInfo: { id: result.transactionId } },
          options: { metadata: { transactionId: result.transactionId } },
          nonExpendableDocumentNumber:
            (payload.requisitionNumber as string) ||
            (payload.document_number as string) ||
            undefined,
        });

        if (docResult.outputBuffer) {
          const opened = openBase64PdfInWindow(docResult.outputBuffer, docWindow);
          if (!opened && docResult.downloadUrl) {
            // Inline open failed — fall back to authenticated download
            const fallbackUrl = documentService.resolveDownloadUrl(docResult.downloadUrl);
            if (fallbackUrl) await openDocumentWithAuth(fallbackUrl);
          }
        } else if (docResult.downloadUrl) {
          const url = documentService.resolveDownloadUrl(docResult.downloadUrl);
          if (url) {
            const opened = await openDocumentWithAuth(url, docWindow);
            if (!opened) docFailed = true;
          } else {
            if (docWindow && !docWindow.closed) docWindow.close();
            docFailed = true;
          }
        } else {
          if (docWindow && !docWindow.closed) docWindow.close();
          docFailed = true;
        }

        // Commit the document number reservation after successful generation
        if (reservationId && !docFailed) {
          try {
            await httpClient.post('/api/v1/document-numbers/commit', { reservationId });
          } catch {
            // Best-effort — number is already on the document
          }
        }
      } catch {
        if (docWindow && !docWindow.closed) docWindow.close();
        docFailed = true;
      }

      if (docFailed) {
        notifications.warning(
          'Document Generation Issue',
          `${isTransfer ? 'Transfer' : 'Shipment'} completed but the ${documentType} form could not be opened. You can regenerate it later.`,
        );
      }
    },
    [isTransfer, notifications],
  );

  const handleComplete = useCallback(async () => {
    if (cartCount === 0) return;

    if (isTransfer && !signatureAcknowledged) {
      notifications.error(
        'Signature Required',
        `Capture ${person.label} Signature before completing transfer.`,
      );
      return;
    }

    const assetIds = cartItems.flatMap((item) => item.assetIds);

    // Build document payload from cart items (capture before mutation may trigger re-renders)
    const documentType = isTransfer ? 'DA3161' : 'DD1149';
    const yyyymmdd = toYYYYMMDD();

    let payload: Record<string, unknown>;

    if (isTransfer) {
      // Reserve a document number for the lateral transfer DA 3161
      let documentNumber = '';
      let reservationId: string | null = null;
      try {
        const reservation = await httpClient.post<{
          reservationId: string;
          nonExpendableNumber?: string;
          expendableNumber?: string;
        }>('/api/v1/document-numbers/reserve', {
          transactionId: crypto.randomUUID(),
          transactionType: 'lateral_transfer',
          needsNonExpendable: true,
          needsExpendable: false,
        });
        documentNumber = reservation.nonExpendableNumber ?? '';
        reservationId = reservation.reservationId;
      } catch {
        // Proceed without a document number — field will be blank
      }

      // Fetch catalog prices and unit of issue for lateral transfer line items
      const catalogIds = [
        ...new Set(cartItems.map((i) => i.catalogId).filter((id): id is string => !!id)),
      ];
      const { priceMap, unitOfIssueMap } = await fetchCatalogData(catalogIds);

      const lineItems: {
        line_number: string;
        stock_number: string;
        description: string;
        unit_of_issue: string;
        quantity: number | string;
        code: string;
        supply_action: number | string;
        actual_price: number | undefined;
        total_cost: number | undefined;
      }[] = cartItems.map((item, idx) => {
        const unitPrice = (item.catalogId && priceMap.get(item.catalogId)) || 0;
        const totalCost = unitPrice > 0 ? unitPrice * item.quantity : 0;
        const unitOfIssue = (item.catalogId && unitOfIssueMap.get(item.catalogId)) || 'EA';
        return {
          line_number: String(idx + 1),
          stock_number: [item.lin, item.nsn].filter((v) => v && v !== '—').join('\n'),
          description: item.name,
          unit_of_issue: unitOfIssue,
          quantity: item.quantity,
          code: 'LT',
          supply_action: item.quantity,
          actual_price: unitPrice > 0 ? unitPrice : undefined,
          total_cost: totalCost > 0 ? totalCost : undefined,
        };
      });

      const grandTotal = lineItems.reduce((sum, i) => {
        const cost = typeof i.total_cost === 'number' ? i.total_cost : 0;
        return sum + cost;
      }, 0);
      const items = buildDa3161ItemsWithReasonAtRow4(lineItems, transferReasonStatement);

      // Pad items array to 10 rows (DA3161 template capacity)
      const DA3161_TOTAL_ROWS = 10;
      while (items.length < DA3161_TOTAL_ROWS) {
        items.push({
          line_number: '',
          stock_number: '',
          description: '',
          unit_of_issue: '',
          quantity: '',
          code: '',
          supply_action: '',
          actual_price: undefined,
          total_cost: undefined,
        });
      }

      pendingReservationIdRef.current = reservationId;

      payload = {
        document_number: documentNumber,
        voucher_number: '',
        send_to: '',
        issue_from: distributionLabels.issue_from_default,
        dodaac: 'W912HN',
        transaction_date: yyyymmdd,
        issue_type: true,
        turn_in_type: false,
        individualSignature: capturedSignature,
        transfer_reason_option: transferReason,
        transfer_reason_statement: transferReasonStatement,
        transfer_hr_from: hrFrom.trim() || null,
        transfer_hr_to: hrTo.trim() || null,
        transfer_reason_yellow_box: transferReasonStatement,
        grand_total: grandTotal,
        items,
      };
    } else {
      // Reserve a document number for DD1149 shipment (box 3 requisition number)
      let requisitionNumber = '';
      let reservationId: string | null = null;
      try {
        const reservation = await httpClient.post<{
          reservationId: string;
          nonExpendableNumber?: string;
          expendableNumber?: string;
        }>('/api/v1/document-numbers/reserve', {
          transactionId: crypto.randomUUID(),
          transactionType: 'issue',
          needsNonExpendable: true,
          needsExpendable: false,
        });
        requisitionNumber = reservation.nonExpendableNumber ?? '';
        reservationId = reservation.reservationId;
      } catch {
        // Proceed without a document number — box 3 will be blank
      }

      pendingReservationIdRef.current = reservationId;

      // Fetch catalog prices and unit of issue for shipment line items (DD1149)
      const catalogIds = [
        ...new Set(cartItems.map((i) => i.catalogId).filter((id): id is string => !!id)),
      ];
      const { priceMap, unitOfIssueMap } = await fetchCatalogData(catalogIds);

      const items: {
        itemNumber: string;
        stockNumber: string;
        description: string;
        unitOfIssue: string;
        quantity: number | string;
        unitPrice: string | undefined;
        totalCost: string | undefined;
      }[] = cartItems.map((item, index) => {
        const unitPrice = (item.catalogId && priceMap.get(item.catalogId)) || 0;
        const totalCost = unitPrice > 0 ? unitPrice * item.quantity : 0;
        const unitOfIssue = (item.catalogId && unitOfIssueMap.get(item.catalogId)) || 'EA';

        // Build description with LIN/NSN prefix and size suffix per DD1149 column (b):
        // "FEDERAL STOCK NUMBER, DESCRIPTION, AND CODING OF MATERIEL AND/OR SERVICES"
        // Format: {LIN}/{NSN}\n{NOMENCLATURE} ({SIZE})
        const lin = item.lin !== '—' ? item.lin : '';
        const nsn = item.nsn !== '—' ? item.nsn : '';
        const identifierLine = [lin, nsn].filter(Boolean).join('/');
        const sizeSuffix = item.sizeLabel ? ` (${item.sizeLabel})` : '';
        const descriptionLines = [identifierLine, `${item.name}${sizeSuffix}`]
          .filter(Boolean)
          .join('\n');

        return {
          itemNumber: (index + 1).toString(),
          stockNumber: nsn,
          description: descriptionLines,
          unitOfIssue,
          quantity: item.quantity,
          unitPrice: unitPrice > 0 ? unitPrice.toFixed(2) : undefined,
          totalCost: totalCost > 0 ? totalCost.toFixed(2) : undefined,
        };
      });

      const grandTotal = items.reduce((sum, i) => {
        const cost = i.totalCost && typeof i.totalCost === 'string' ? parseFloat(i.totalCost) : 0;
        return sum + cost;
      }, 0);

      // Calculate total pages: Page 1 holds 5 items, Page 2+ holds 9 items each
      const itemCount = items.length;
      let totalPages = 1; // Always at least 1 page
      if (itemCount > 5) {
        const remainingItems = itemCount - 5;
        const additionalPages = Math.ceil(remainingItems / 9);
        totalPages = 1 + additionalPages;
      }

      // Pad items array to fill all template rows
      const totalTemplateRows = totalPages === 1 ? 5 : 5 + (totalPages - 1) * 9;
      while (items.length < totalTemplateRows) {
        items.push({
          itemNumber: '',
          stockNumber: '',
          description: '',
          unitOfIssue: '',
          quantity: '',
          unitPrice: undefined,
          totalCost: undefined,
        });
      }

      payload = {
        requisitionNumber,
        shipDate: yyyymmdd,
        fromAddress: distributionLabels.dd1149_from_address,
        toAddress: distributionLabels.dd1149_shipping_address,
        items,
        grandTotal,
        totalAmount: grandTotal,
        sheetNumber: '1', // Sheet number is always 1 for the first page
        totalPages: totalPages.toString(),
      };
    }

    let result: TransfersShippingCompleteResult;
    try {
      result = await completeMutation.mutateAsync({ assetIds });
    } catch {
      // Error notification handled by mutation's onError
      return;
    }

    pendingResultRef.current = result;
    pendingPayloadRef.current = { documentType, payload };

    // Clear cart after successful completion
    if (userId && cartData?.cart) {
      try {
        await clearCart({ userId, cartId: cartData.cart.id });
      } catch {
        /* best-effort */
      }
    }

    // Try to fetch editable fields preview; if the document type has no
    // user-editable fields the endpoint returns 400 — skip straight to generation.
    setIsLoadingPreview(true);
    try {
      const preview = await documentService.getDocumentPreview({
        documentType,
        payload,
      });
      setIsLoadingPreview(false);
      setPreviewData(preview);
      setFieldsModalOpen(true);
    } catch {
      // No editable fields or preview unavailable — generate directly
      setIsLoadingPreview(false);

      if (isTransfer) {
        notifications.error(
          'Document Generation Blocked',
          'DA 3161 justification is required and the review step is unavailable. Please retry.',
        );
        return;
      }

      setIsGeneratingPdf(true);
      await generateAndOpen(documentType, payload, result);
      setIsGeneratingPdf(false);
      notifications.success(
        isTransfer ? 'Transfer Completed' : 'Shipment Completed',
        `${cartCount} item(s) processed. Document generated.`,
      );
      setSearchText('');
      setQtyDrafts({});
      setSignatureCaptureOpen(false);
      setCapturedSignature(null);
      setSignatureAcknowledged(false);
      onClose();
    }
  }, [
    cartItems,
    cartCount,
    completeMutation,
    isTransfer,
    signatureAcknowledged,
    userId,
    cartData?.cart,
    clearCart,
    generateAndOpen,
    transferReasonStatement,
    notifications,
    onClose,
  ]);

  const handleFieldsConfirm = useCallback(
    async (editedPayload: Record<string, unknown>) => {
      const result = pendingResultRef.current;
      if (!result) return;

      if (isTransfer && !transferReasonValidation.valid) {
        setShowTransferReasonErrors(true);
        notifications.error(
          'Missing Required Fields',
          'Please complete DA 3161 transfer reason details.',
        );
        return;
      }

      if (isTransfer && !signatureAcknowledged) {
        notifications.error(
          'Signature Required',
          `Capture ${person.label} Signature before generating DA 3161.`,
        );
        return;
      }

      const finalizedPayload = {
        ...editedPayload,
        transaction_date: toYYYYMMDD(),
        individualSignature: capturedSignature,
        transfer_reason_option: transferReason,
        transfer_reason_statement: transferReasonStatement,
        transfer_hr_from: hrFrom.trim() || null,
        transfer_hr_to: hrTo.trim() || null,
        transfer_reason_yellow_box: transferReasonStatement,
      };

      setIsGeneratingPdf(true);
      const documentType = isTransfer ? 'DA3161' : 'DD1149';
      await generateAndOpen(documentType, finalizedPayload, result);
      setIsGeneratingPdf(false);
      setFieldsModalOpen(false);
      setPreviewData(null);

      setSearchText('');
      setQtyDrafts({});
      setTransferReason('');
      setHrFrom('');
      setHrTo('');
      setOtherReason('');
      setShowTransferReasonErrors(false);
      setCapturedSignature(null);
      setSignatureAcknowledged(false);
      onClose();
    },
    [
      isTransfer,
      transferReasonValidation.valid,
      signatureAcknowledged,
      notifications,
      transferReason,
      transferReasonStatement,
      hrFrom,
      hrTo,
      capturedSignature,
      generateAndOpen,
      onClose,
    ],
  );

  const handleFieldsSkip = useCallback(async () => {
    const docWindow = docWindowRef.current;
    if (docWindow && !docWindow.closed) docWindow.close();

    // Save the document payload so the document can be regenerated later
    const pending = pendingPayloadRef.current;
    const result = pendingResultRef.current;
    if (pending) {
      try {
        await documentService.saveDocumentPayload({
          documentType: pending.documentType,
          payload: {
            ...pending.payload,
            ...(result?.transactionId ? { transactionInfo: { id: result.transactionId } } : {}),
          },
          transactionId: result?.transactionId ?? null,
          documentNumber: (pending.payload.requisitionNumber as string) ||
            (pending.payload.document_number as string) || null,
        });
      } catch {
        // Best-effort — transaction is already committed
      }
    }

    setFieldsModalOpen(false);
    setPreviewData(null);
    setSearchText('');
    setQtyDrafts({});
    setTransferReason('');
    setHrFrom('');
    setHrTo('');
    setOtherReason('');
    setShowTransferReasonErrors(false);
    setCapturedSignature(null);
    setSignatureAcknowledged(false);
    onClose();
  }, [onClose]);

  const handleFieldsClose = useCallback(() => {
    const docWindow = docWindowRef.current;
    if (docWindow && !docWindow.closed) docWindow.close();
    setFieldsModalOpen(false);
    setPreviewData(null);
    setSearchText('');
    setQtyDrafts({});
    setTransferReason('');
    setHrFrom('');
    setHrTo('');
    setOtherReason('');
    setShowTransferReasonErrors(false);
    setCapturedSignature(null);
    setSignatureAcknowledged(false);
    onClose();
  }, [onClose]);

  const transferReasonContent = isTransfer ? (
    <Stack spacing={2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        DA 3161 Transfer Reason
      </Typography>

      <FormControl
        size="small"
        fullWidth
        error={showTransferReasonErrors && transferReasonValidation.reasonError}
      >
        <InputLabel id="da3161-transfer-reason-label">Reason</InputLabel>
        <Select
          labelId="da3161-transfer-reason-label"
          value={transferReason}
          label="Reason"
          onChange={(event) => setTransferReason(event.target.value as TransferReasonKey | '')}
        >
          {TRANSFER_REASON_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {showTransferReasonErrors && transferReasonValidation.reasonError && (
          <FormHelperText>Reason is required.</FormHelperText>
        )}
      </FormControl>

      {requiresHrValues && (
        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
          <TextField
            size="small"
            label="From HR"
            value={hrFrom}
            onChange={(event) => setHrFrom(event.target.value)}
            required
            error={showTransferReasonErrors && transferReasonValidation.hrFromError}
            helperText={
              showTransferReasonErrors && transferReasonValidation.hrFromError
                ? 'From HR is required.'
                : undefined
            }
            fullWidth
          />
          <TextField
            size="small"
            label="To HR"
            value={hrTo}
            onChange={(event) => setHrTo(event.target.value)}
            required
            error={showTransferReasonErrors && transferReasonValidation.hrToError}
            helperText={
              showTransferReasonErrors && transferReasonValidation.hrToError
                ? 'To HR is required.'
                : undefined
            }
            fullWidth
          />
        </Stack>
      )}

      {transferReason === 'other' && (
        <TextField
          size="small"
          label="Other Reason"
          value={otherReason}
          onChange={(event) => setOtherReason(event.target.value)}
          required
          error={showTransferReasonErrors && transferReasonValidation.otherError}
          helperText={
            showTransferReasonErrors && transferReasonValidation.otherError
              ? 'Other reason is required.'
              : undefined
          }
          fullWidth
          multiline
          minRows={2}
        />
      )}
    </Stack>
  ) : null;

  const plannerToolbar = (
    <TextField
      value={searchText}
      onChange={(event) => setSearchText(event.target.value)}
      placeholder="Search assets, LIN, NSN, location..."
      size="small"
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  const columns: GridColDef<AssetInventoryGroup>[] = [
    { field: 'asset_name', headerName: 'Asset Name', flex: 1, minWidth: 220 },
    { field: 'lin', headerName: 'LIN', width: 120 },
    { field: 'nsn', headerName: 'NSN', width: 150 },
    {
      field: 'location_name',
      headerName: 'Location',
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <Stack justifyContent="center" sx={{ height: '100%' }}>
          <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
            {params.value}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.2, fontSize: '0.7rem' }}
          >
            {params.row.location_path}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'stock_quantity',
      headerName: 'Available Qty',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
    },
    {
      field: 'qty',
      headerName: 'Qty',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const remainingQty = getAddableQty(row);
        const draft = qtyDrafts[row.id];
        const value =
          remainingQty <= 0 ? 0 : draft != null ? clampQuantity(draft, remainingQty) : remainingQty;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <TextField
              size="small"
              type="number"
              value={value}
              disabled={remainingQty <= 0}
              slotProps={{ htmlInput: { min: 1, max: remainingQty } }}
              onChange={(event) => handleQtyDraftChange(row.id, event.target.value, remainingQty)}
              sx={{ width: 84 }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row;
        const remainingQty = getAddableQty(row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Add fontSize="small" />}
              disabled={remainingQty <= 0 || isPending}
              onClick={() => void handleAddToCart(row)}
            >
              Add
            </Button>
          </Box>
        );
      },
    },
  ];

  const plannerContent = isError ? (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Failed to load available inventory.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => void refetch()}>
            Retry
          </Button>
        </Box>
      </Stack>
    </Paper>
  ) : (
    <Box sx={{ height: '100%', width: '100%' }}>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        loading={isLoading}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableRowSelectionOnClick
        rowHeight={60}
      />
    </Box>
  );

  const sidebarContent = (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Cart
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, maxHeight: '40vh', overflowY: 'auto' }}>
          <Stack spacing={1.5}>
            {cartItems.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                No items added yet
              </Typography>
            ) : (
              cartItems.map((item) => (
                <WorkflowCartRow
                  key={item.key}
                  title={item.name}
                  subtitle={[
                    item.sizeLabel ? `Size ${item.sizeLabel}` : null,
                    item.lin !== '—' ? `LIN ${item.lin}` : null,
                    item.nsn !== '—' ? `NSN ${item.nsn}` : null,
                    `Qty ${item.quantity}`,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                  actions={
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={`Remove ${item.name} from cart`}
                      onClick={() => void handleRemoveCartItem(item.assetIds)}
                      disabled={isPending}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  }
                />
              ))
            )}
          </Stack>
        </Paper>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Total Items
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {cartCount} item{cartCount === 1 ? '' : 's'} selected
        </Typography>
      </Box>

      {isTransfer && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {person.label} Signature
          </Typography>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleCaptureSignature}
            disabled={cartCount === 0 || isPending || isLoadingPreview || isGeneratingPdf}
          >
            {signatureAcknowledged && signaturePreviewSrc
              ? 'Signature Captured'
              : signatureAcknowledged
                ? 'Signature Skipped'
                : `Capture ${person.label} Signature`}
          </Button>
          {!signatureAcknowledged && (
            <Button
              variant="text"
              size="small"
              fullWidth
              onClick={handleSkipSignature}
              disabled={cartCount === 0 || isPending || isLoadingPreview || isGeneratingPdf}
              sx={{ mt: -0.5 }}
            >
              Skip Signature
            </Button>
          )}
          {signaturePreviewSrc ? (
            <Paper variant="outlined" sx={{ mt: 1, p: 1.5, bgcolor: '#ffffff' }}>
              <Box
                component="img"
                src={signaturePreviewSrc}
                alt={`${person.label} signature preview`}
                sx={{ width: '100%', maxHeight: 72, objectFit: 'contain' }}
              />
              <Button
                size="small"
                onClick={handleClearSignature}
                disabled={isPending || isLoadingPreview || isGeneratingPdf}
                sx={{ mt: 1 }}
              >
                Clear Signature
              </Button>
            </Paper>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Captured signature is applied to DA 3161 red-box signature fields.
            </Typography>
          )}
          {!signatureAcknowledged && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              Signature or skip is required to complete transfer.
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );

  const footer = (
    <>
      <Button variant="outlined" onClick={handleClose} disabled={isPending}>
        Cancel
      </Button>
      <ViewerGuardedButton
        variant="contained"
        onClick={() => void handleComplete()}
        disabled={
          isPending ||
          isLoadingPreview ||
          isGeneratingPdf ||
          cartCount === 0 ||
          (isTransfer && !signatureAcknowledged)
        }
      >
        {completeMutation.isPending
          ? 'Completing…'
          : isLoadingPreview || isGeneratingPdf
            ? 'Generating document...'
            : `Complete ${isTransfer ? 'Transfer' : 'Shipment'}`}
      </ViewerGuardedButton>
    </>
  );

  const closeButton = (
    <Button
      variant="outlined"
      startIcon={<WestIcon />}
      onClick={handleClose}
      sx={{ whiteSpace: 'nowrap' }}
    >
      Return to Transfers & Shipping
    </Button>
  );

  return (
    <>
      <WorkflowModalLayout
        open={open}
        onClose={handleClose}
        title={`New ${isTransfer ? 'Transfer' : 'Shipment'}`}
        plannerTitle="In-Stock Inventory"
        plannerToolbar={plannerToolbar}
        plannerContent={plannerContent}
        sidebarTitle={`${isTransfer ? 'Transfer' : 'Shipment'} Summary`}
        sidebarContent={sidebarContent}
        footer={footer}
        closeButton={closeButton}
      />
      <DocumentFieldsModal
        open={fieldsModalOpen}
        onClose={handleFieldsClose}
        onConfirm={handleFieldsConfirm}
        onSkip={isTransfer ? undefined : handleFieldsSkip}
        previewData={previewData}
        isGenerating={isGeneratingPdf}
        appendContent={transferReasonContent}
        isConfirmDisabled={
          isTransfer && (!transferReasonValidation.valid || !signatureAcknowledged)
        }
        title={
          isTransfer ? 'DA 3161 — Request for Issue/Turn-In' : 'DD 1149 — Requisition and Invoice'
        }
        subtitle={
          isTransfer
            ? 'Review and complete required transfer reason fields before generating the document.'
            : 'Review and fill in any additional fields before generating the document. All fields are optional.'
        }
        confirmLabel="Generate Document"
        skipLabel="Skip Document"
        cancelLabel="Close"
      />

      <SignatureCaptureDialog
        open={signatureCaptureOpen}
        title={`Capture ${person.label} Signature`}
        initialSignature={capturedSignature}
        onCancel={() => setSignatureCaptureOpen(false)}
        onSave={handleSignatureSaved}
      />

      <Dialog
        open={showExitPrompt}
        onClose={() => setShowExitPrompt(false)}
        TransitionProps={{
          onExited: () => {
            if (isForceClosing) {
              onClose();
              setIsForceClosing(false);
            }
          },
        }}
        aria-labelledby="exit-confirmation-title"
        aria-describedby="exit-confirmation-description"
      >
        <DialogTitle id="exit-confirmation-title">Items in Cart</DialogTitle>
        <DialogContent>
          <DialogContentText id="exit-confirmation-description">
            You have items in your {isTransfer ? 'transfer' : 'shipment'} cart. You can save your
            progress for later or cancel to remove the reserved items.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => void handleCancelTransfer()}
            color="error"
            disabled={isCanceling}
          >
            Cancel {isTransfer ? 'Transfer' : 'Shipment'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={() => setShowExitPrompt(false)}
            color="inherit"
            disabled={isCanceling}
          >
            Back to Cart
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            autoFocus
            disabled={isCanceling}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
